import { useCallback, useEffect } from 'react';
import {
  Tooltip,
  Typography,
} from '@forethought-technologies/forethought-elements';
import { IconX } from '@tabler/icons-react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  cancelMobileQuickFeedback,
  selectQuickFeedbackConfig,
  selectQuickFeedbackMobileClickedStepIndex,
  selectWorkflowsConfig,
} from 'reducers/workflows';
import { selectIsMobile } from 'reducers/main';
import { PopupModal } from 'components/PopupModal';

export default function MobileQuickFeedbackModal({
  children,
  stepIndex,
}: {
  children: JSX.Element;
  stepIndex: number;
}): JSX.Element {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const isMobile = useAppSelector(selectIsMobile);
  const quickFeedbackConfig = useAppSelector(selectQuickFeedbackConfig);
  const quickFeedbackMobileClickedStepIndex = useAppSelector(
    selectQuickFeedbackMobileClickedStepIndex,
  );
  const workflowsConfig = useAppSelector(selectWorkflowsConfig);

  const handleCancel = useCallback(() => {
    dispatch(cancelMobileQuickFeedback());
  }, [dispatch]);

  // Close modal when leaving mobile mode:
  useEffect(() => {
    if (!isMobile) {
      handleCancel();
    }
  }, [handleCancel, isMobile]);

  const isVisible = quickFeedbackMobileClickedStepIndex === stepIndex;

  if (!quickFeedbackConfig || !workflowsConfig || !isVisible) {
    return <></>;
  }

  return (
    <PopupModal onClickAway={handleCancel} open={isVisible} position='bottom'>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='font16Bold'>
            {quickFeedbackConfig.feedback_prompt}
          </Typography>
          <Tooltip
            tooltipContent={
              workflowsConfig.forethought_text?.close_button_title || ''
            }
          >
            <Box
              aria-label={workflowsConfig.forethought_text?.close_button_title}
              component='button'
              onClick={handleCancel}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                color: theme.palette.colors.grey[800],
                cursor: 'pointer',
                height: '20px',
                padding: 0,
              }}
              type='button'
            >
              <IconX size={20} />
            </Box>
          </Tooltip>
        </Box>
        <Box sx={{ alignSelf: 'center', display: 'flex' }}>{children}</Box>
      </Box>
    </PopupModal>
  );
}
